/**
 * «На чём основан ответ» — ссылка на ДАННЫЕ, которые агент действительно читал (PROP-456).
 *
 * ЗАЧЕМ. Агент отвечает «денег столько-то», и проверить это нечем: в «Отладке» видно строку
 * `Вызов: cubus_query`, но не видно, ЧТО он спросил и как открыть то же самое глазами. Ссылка
 * снимает ровно этот разрыв: она ведёт на объект с ТЕМ ЖЕ фильтром, группировкой и сортировкой,
 * с какими работал агент.
 *
 * ⚠️ ССЫЛКА СТРОИТСЯ ИЗ АРГУМЕНТОВ ВЫЗОВА, А НЕ ИЗ ТЕКСТА ОТВЕТА. Ссылку, написанную моделью,
 * проверять нечем — она может вести не туда и не на то (`BUG-439`: пять правдоподобных адресов
 * и ни одного рабочего). Аргументы вызова — это то, что ушло в платформу и вернулось данными;
 * ссылка из них воспроизводит выборку, а не пересказывает её.
 *
 * ⚠️ АРГУМЕНТЫ ТОЖЕ ПРИДУМАЛА МОДЕЛЬ. Поэтому здесь всё проверяется по форме, ничего не
 * достраивается «по смыслу», и при малейшем сомнении ссылки просто нет: отсутствие ссылки
 * честнее ссылки, ведущей не туда.
 */

import { encodeFilter, isFilterShape } from '@/cubus/services/urlFilter'
import { isGroupbyShape, isOrderbyShape } from '@/cubus/services/urlView'

/** Сколько источников показываем под ответом. Больше — это уже не сводка, а простыня. */
export const MAX_SOURCES = 5

/**
 * Адрес не резиновый. Фильтр на десяток условий влезает свободно; всё, что длиннее, — признак,
 * что агент собрал что-то необычное, и ссылка на это всё равно бесполезна человеку.
 */
const MAX_HREF = 4000

/** Тулы, у которых есть что показать человеку. Остальные (правки схемы, служебные) — не про данные. */
const DATA_TOOLS = new Set([
  'cubus_query',
  'cubus_calc_record',
  'cubus_new_record',
  'cubus_save_record',
  'cubus_delete_record',
  'cubus_run_mount',
  'cubus_mount_steps',
  'cubus_run_report',
  'cubus_report_params',
])

/** `mcp__cubus__cubus_query` → `cubus_query`. Имя приходит от Claude Code в его собственной форме. */
export function bareToolName(name) {
  const raw = String(name || '')
  const m = /^mcp__[^_]+__(.+)$/.exec(raw)
  return m ? m[1] : raw
}

/**
 * ⚠️ ДВА НАПИСАНИЯ ПРИЗНАКА «ВКЛЮЧЕНО» — не небрежность, а разные стороны платформы: схема тула
 * `cubus_query` называет его `isactive`, а таблица во фронте смотрит на `isActive`. Ссылка
 * пересекает границу между ними, поэтому проставляем ОБА: иначе сервер сгруппирует данные, а
 * панель покажет группировку выключенной — человек увидит цифры и не поймёт, откуда они.
 */
/**
 * Фильтр к виду, который примет страница, — или `null`, если принимать нечего.
 *
 * ⚠️ Пустой `nodes` ДОСТАВЛЯЕТСЯ. Найдено живым прогоном ассистента (PROP-457): модель пишет
 * `conds` и `oper`, а `nodes` не пишет — и страница такой фильтр отбрасывает (`isFilterShape`
 * требует оба массива), хотя по смыслу он полон: вложенных подгрупп просто нет. Без этой правки
 * ссылка вела бы на объект БЕЗ фильтра, то есть показывала не ту выборку под видом той же.
 */
/**
 * PROP-463: у каждого аргумента-поля есть ЗНАЧЕНИЕ (номер поля), во всех условиях и подгруппах.
 * Условие без него страница проигнорирует — и ссылка показала бы не ту выборку под видом той же.
 * То же правило, что у серверной копии (`ToolSource.entityArgsHaveValue`).
 */
function usableValue(v) {
  if (typeof v === 'number') return true
  if (typeof v === 'string') return v.trim() !== ''
  if (v && typeof v === 'object' && !Array.isArray(v)) return usableValue(v.value)
  return false
}

function entityArgsHaveValue(node) {
  const conds = Array.isArray(node.conds) ? node.conds : []
  const ok = conds.every(c => (Array.isArray(c && c.args) ? c.args : [])
    .every(a => !(a && a.type && a.type.sid === 'entity') || usableValue(a.value)))
  return ok && (Array.isArray(node.nodes) ? node.nodes : []).every(n => n && typeof n === 'object' && entityArgsHaveValue(n))
}

function filterForUrl(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const { node } = raw
  if (!node || typeof node !== 'object') return null
  if (!Array.isArray(node.conds) || !node.oper || typeof node.oper !== 'object') return null
  if (!entityArgsHaveValue(node)) return null
  const full = { ...raw, node: { ...node, nodes: Array.isArray(node.nodes) ? node.nodes : [] } }
  return isFilterShape(full) ? full : null
}

function activeView(view) {
  return { ...view, isActive: true, isactive: true }
}

/** Ключ объекта из аргументов: годится и числовой id, и sid (маршрут принимает оба, BUG-387). */
function objectKey(input) {
  const id = input.objectid
  if (typeof id === 'number' && Number.isInteger(id) && id > 0) return String(id)
  if (typeof id === 'string' && /^\d+$/.test(id.trim())) return id.trim()
  const sid = input.objectsid
  if (typeof sid === 'string' && /^[A-Za-z0-9._-]{1,80}$/.test(sid.trim())) return sid.trim()
  return ''
}

/** Идентификатор записи — если вызов был про одну запись, а не про выборку. */
function recordKey(input) {
  const direct = input.recordid
  if (typeof direct === 'number' && Number.isInteger(direct) && direct > 0) return String(direct)
  if (typeof direct === 'string' && /^\d+$/.test(direct.trim())) return direct.trim()
  const rec = input.record
  if (rec && typeof rec === 'object') {
    const id = rec.id && typeof rec.id === 'object' ? rec.id.value : rec.id
    if (typeof id === 'number' && Number.isInteger(id) && id > 0) return String(id)
    if (typeof id === 'string' && /^\d+$/.test(id.trim())) return id.trim()
  }
  return ''
}

/**
 * Вызов инструмента → источник данных для человека.
 *
 * @param call        `{ name, input }` — как их прислал агент.
 * @param domainSid   домен, в котором человек сейчас работает.
 * @returns `{ tool, kind, label, href }` либо `null`, если показывать нечего.
 */
export function sourceOfToolCall(call, domainSid = '') {
  if (!call || typeof call !== 'object') return null
  const tool = bareToolName(call.name)
  if (!DATA_TOOLS.has(tool)) return null
  const input = (call.input && typeof call.input === 'object' && !Array.isArray(call.input)) ? call.input : {}

  // ⚠️ ЧУЖОЙ ДОМЕН — БЕЗ ССЫЛКИ. Агент вправе позвать тул с `domainsid`, но страница откроется в
  // домене человека и покажет ДРУГИЕ данные под видом тех же. Молча увести не туда хуже, чем
  // не дать ссылку вовсе.
  const asked = typeof input.domainsid === 'string' ? input.domainsid.trim() : ''
  if (asked && domainSid && asked !== domainSid) return null

  const key = objectKey(input)
  if (!key) return null

  const rec = recordKey(input)
  if (rec) {
    const named = /^\d+$/.test(key) ? `объекте ${key}` : `объекте «${key}»`
    return {
      tool, kind: 'record', label: `запись ${rec} в ${named}`, href: `/cubus/recordpage/${key}/${rec}`,
    }
  }

  const params = []
  const filter = filterForUrl(input.filter)
  if (filter) {
    const enc = encodeFilter(filter)
    if (enc) params.push(`filter=${enc}`)
  }
  if (isGroupbyShape(input.groupby) && input.groupby.groups.length) {
    const enc = encodeFilter(activeView(input.groupby))
    if (enc) params.push(`groupby=${enc}`)
  }
  if (isOrderbyShape(input.orderby) && input.orderby.sorts.length) {
    const enc = encodeFilter(activeView(input.orderby))
    if (enc) params.push(`orderby=${enc}`)
  }
  const href = `/cubus/object/${key}${params.length ? `?${params.join('&')}` : ''}`
  if (href.length > MAX_HREF) {
    // Фильтр в адрес не поместился. Ссылка на объект БЕЗ фильтра была бы обманом — показала бы
    // не ту выборку под видом той же, — поэтому не даём ничего.
    return null
  }
  const filtered = params.length > 0
  // Числовой ключ и sid читаются по-разному: «объект 8» — это адрес, «объект «role»» — имя.
  const named = /^\d+$/.test(key) ? `объект ${key}` : `объект «${key}»`
  return {
    tool,
    kind: filtered ? 'query' : 'object',
    label: filtered ? `${named} с тем же отбором` : named,
    href,
  }
}

/**
 * Копим источники по ходу работы: один и тот же адрес — один источник, порядок сохраняем,
 * храним последние `MAX_SOURCES`. Агент зовёт тул и десять раз подряд, и сводка из десяти
 * одинаковых строк отвечает на вопрос хуже, чем одна.
 */
export function addSource(list, source) {
  if (!source) return list
  const out = list.filter(s => s.href !== source.href)
  out.push(source)
  return out.slice(-MAX_SOURCES)
}

/** Сводка под ответом. Пусто — значит агент данных не читал, и говорить об этом нечего. */
export function renderSourcesLine(sources) {
  if (!Array.isArray(sources) || sources.length === 0) return ''
  const links = sources.map(s => `[${s.label}](${s.href})`).join(', ')
  return `На чём основан ответ: ${links}`
}
